import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { MaskedLines, Reveal, ChapterHeading } from "@/components/motion";
import { PORTRAIT_URL, CLINIC_URL } from "@/constants/site";

const WHAT_WE_DO = [
  "Review the project location and identify the shielding or plan-review path that applies.",
  "Review the exact imaging equipment, room layout, orientation, workload, construction, and occupancy around the room.",
  "Prepare project-specific radiation shielding calculations.",
  "Provide barrier and material recommendations coordinated with the construction documents.",
  "Coordinate shielding questions with the facility, architect, contractor, and equipment vendor.",
  "Organize shielding-related technical information for state or local plan review when that process applies.",
];

const WHO_WE_SERVE = [
  "Medical and dental practices planning new imaging rooms or equipment replacements.",
  "Architects and engineers coordinating diagnostic imaging spaces.",
  "General contractors and shielding installers building or remodeling X-ray rooms.",
  "X-ray equipment vendors and installers who need a physicist behind a customer project.",
  "Hospitals and outpatient imaging groups planning X-ray, CT, CBCT, or fluoroscopy projects.",
];

const About = () => (
  <>
    <section data-testid="about-hero" className="border-b border-border bg-blueprint-grid pt-16">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 pb-16 pt-14 md:px-8 md:pb-24 md:pt-20 lg:grid-cols-[1fr_0.8fr] lg:items-center">
        <div>
          <Reveal y={12}>
            <p className="eyebrow">About the practice</p>
          </Reveal>
          <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLines delay={0.1} lines={["Physicist-led.", "Purposely", "focused."]} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-ink/70 md:text-lg" data-testid="about-intro">
              Heartland Physics is a physicist-led consulting practice focused on radiation shielding design for diagnostic imaging equipment that uses X-rays — general radiography, CT, cone-beam CT, fluoroscopy and C-arm systems, and dental X-ray and CBCT projects.
            </p>
          </Reveal>
          <Reveal delay={0.65} className="mt-9">
            <Link
              to="/request"
              data-testid="about-cta-request"
              className="group inline-flex items-center gap-2 bg-blueprint px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink"
            >
              Request a Shielding Design
              <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>

        <Reveal delay={0.35} y={16}>
          <div className="relative" data-testid="about-portrait-frame">
            <div className="spotlight absolute -inset-8" />
            <div className="clip-chamfer relative border border-border bg-white p-2">
              <div className="clip-chamfer aspect-[4/5] w-full bg-[#EDF2F9] bg-blueprint-grid">
                <img
                  src={PORTRAIT_URL}
                  alt="Portrait of Lancer Smith, M.S., principal physicist"
                  data-testid="about-portrait"
                  className="h-full w-full object-contain object-bottom"
                />
              </div>
            </div>
            <div className="absolute -bottom-4 left-6 border border-border bg-white px-5 py-3">
              <p className="font-heading text-sm font-bold text-ink">Lancer Smith, M.S.</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Principal Physicist — Heartland Physics</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="border-b border-border bg-white">
      <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-28">
        <Reveal>
          <ChapterHeading number="01" eyebrow="Direct access" title="Work directly with Lancer Smith, M.S." />
        </Reveal>
        <div className="mt-10 grid gap-10 lg:grid-cols-2 lg:gap-20">
          <Reveal delay={0.1}>
            <p className="text-base leading-relaxed text-ink/70">
              Lancer works directly with facility owners, architects, contractors, equipment vendors, and imaging teams. There is no handoff to a sales team. The person reviewing the project information is the person responsible for the shielding calculations and technical recommendations.
            </p>
            <p className="mt-5 text-base leading-relaxed text-ink/70">
              Every report is handled directly by Lancer from project intake through final delivery — a principal-led practice by design, not by accident.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden border border-border">
              <img
                src={CLINIC_URL}
                alt="A real diagnostic X-ray room with ceiling-mounted tube, table, and wall bucky"
                loading="lazy"
                data-testid="about-clinic-image"
                className="h-64 w-full object-cover contrast-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/45 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-5 font-mono text-[10px] uppercase tracking-[0.22em] text-white/85">
                Facility owners · architects · contractors · vendors
              </p>
              <p className="absolute right-5 top-4 font-mono text-[9px] tracking-wide text-white/60">
                Photo: Wikimedia Commons (CC BY-SA 4.0)
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="border-b border-border bg-blueprint-grid">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 md:px-8 md:py-28 lg:grid-cols-2">
        <div>
          <Reveal>
            <ChapterHeading number="02" eyebrow="Scope" title="What Heartland Physics does" />
          </Reveal>
          <div className="mt-10 flex flex-col gap-4">
            {WHAT_WE_DO.map((item, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="flex items-start gap-3 border border-border bg-white p-4" data-testid={`what-item-${i}`}>
                  <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-blueprint" />
                  <p className="text-sm leading-relaxed text-ink/75">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div>
          <Reveal delay={0.1}>
            <ChapterHeading number="03" eyebrow="Clients" title="Who Heartland Physics works with" />
          </Reveal>
          <div className="mt-10 flex flex-col gap-4">
            {WHO_WE_SERVE.map((item, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="flex items-start gap-3 border border-border bg-white p-4" data-testid={`who-item-${i}`}>
                  <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-blueprint" />
                  <p className="text-sm leading-relaxed text-ink/75">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="bg-white">
      <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
        <Reveal>
          <div className="border border-blueprint/30 bg-blueprint/5 p-8 md:p-10" data-testid="about-disclaimer">
            <p className="eyebrow">A note on scope</p>
            <h2 className="mt-4 font-heading text-lg font-extrabold tracking-tight text-ink">What a shielding design is — and what it is not</h2>
            <p className="mt-5 max-w-3xl text-sm leading-relaxed text-ink/70">
              A shielding design is project-specific technical work based on the actual equipment, expected use, room geometry, construction, and occupancy. It is not a regulatory approval and it does not replace facility registration, equipment testing, inspections, radiation surveys, or other requirements that may apply in a particular jurisdiction. State and local requirements vary — the current requirements of the responsible radiation-control authority always control.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="mt-10">
          <Link
            to="/contact"
            data-testid="about-cta-contact"
            className="group inline-flex items-center gap-2 border border-ink/25 px-7 py-4 text-sm font-semibold text-ink transition-colors duration-200 hover:border-blueprint hover:text-blueprint"
          >
            Get in touch
            <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  </>
);

export default About;
